/**
 * WorkoutLibrary Page
 * Browse bike workouts from the database with filters, search, and infinite scroll
 */

import { ArrowBack, DirectionsBike, Favorite, FilterList, Search } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../services/api';
import { logger } from '../../utils/logger';
import { LoadingAnimation } from '../common/LoadingAnimation';
import { RangeInputWithSlider } from '../common/RangeInputWithSlider';
import { WorkoutCard, WorkoutCardData } from '../workout/WorkoutCard';

interface DurationRange {
  min: number;
  max: number;
}

interface LoadRange {
  min: number;
  max: number;
}

interface Filters {
  name: string;
  category: string;
  durationRange: DurationRange;
  loadRange: LoadRange;
  favoritesOnly: boolean;
}

const DURATION_MARKS = [
  { value: 0, label: '0' },
  { value: 30, label: '30m' },
  { value: 60, label: '1h' },
  { value: 90, label: '90m' },
  { value: 120, label: '2h' },
  { value: 180, label: '3h' },
];

const LOAD_MARKS = [
  { value: 0, label: '0' },
  { value: 50, label: '50' },
  { value: 100, label: '100' },
  { value: 150, label: '150' },
  { value: 200, label: '200' },
];

const PAGE_SIZE = 10;

export const WorkoutLibrary: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Data state
  const [workouts, setWorkouts] = useState<WorkoutCardData[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);

  // Favorites state
  const [favoriteWorkouts, setFavoriteWorkouts] = useState<string[]>([]);
  const [favoritesLoading, setFavoritesLoading] = useState(true);

  // Filter state (what user is editing)
  const [filters, setFilters] = useState<Filters>({
    name: '',
    category: '',
    durationRange: { min: 30, max: 60 },
    loadRange: { min: 30, max: 90 },
    favoritesOnly: false,
  });

  // Applied filters (what's actually being queried)
  const [appliedFilters, setAppliedFilters] = useState<Filters>({
    name: '',
    category: '',
    durationRange: { min: 30, max: 60 },
    loadRange: { min: 30, max: 90 },
    favoritesOnly: false,
  });

  // Infinite scroll observer
  const observerTarget = useRef<HTMLDivElement>(null);

  // Load favorites from user profile
  useEffect(() => {
    const loadFavorites = async () => {
      try {
        const response = await apiClient.getUserProfile();
        if (response.success && response.data) {
          setFavoriteWorkouts(response.data.favoriteWorkouts || []);
        }
      } catch (err) {
        logger.error('Failed to load favorites', err);
      } finally {
        setFavoritesLoading(false);
      }
    };
    loadFavorites();
  }, []);

  // Toggle favorite handler with optimistic update
  const handleToggleFavorite = async (workoutId: string) => {
    // Optimistic update - toggle immediately for responsive UI
    const previousFavorites = [...favoriteWorkouts];
    const isCurrentlyFavorite = favoriteWorkouts.includes(workoutId);

    if (isCurrentlyFavorite) {
      setFavoriteWorkouts(prev => prev.filter(id => id !== workoutId));
    } else {
      setFavoriteWorkouts(prev => [...prev, workoutId]);
    }

    try {
      const response = await apiClient.toggleFavoriteWorkout(workoutId);
      if (response.success && response.data) {
        // Sync with server response
        setFavoriteWorkouts(response.data.favoriteWorkouts);
        logger.info('Favorite toggled', { workoutId, isFavorite: response.data.isFavorite });
      } else {
        // Revert on failure
        setFavoriteWorkouts(previousFavorites);
        logger.error('Failed to toggle favorite - reverting', { workoutId });
      }
    } catch (err) {
      // Revert on error
      setFavoriteWorkouts(previousFavorites);
      logger.error('Failed to toggle favorite', err);
    }
  };

  // Fetch workouts from API
  const fetchWorkouts = useCallback(
    async (skip: number, resetList: boolean = false) => {
      try {
        if (resetList) {
          setLoading(true);
        } else {
          setLoadingMore(true);
        }
        setError(null);

        const params: {
          skip: number;
          limit: number;
          name?: string;
          category?: string;
          durationMin?: number;
          durationMax?: number;
          loadMin?: number;
          loadMax?: number;
          favoritesOnly?: boolean;
        } = {
          skip,
          limit: PAGE_SIZE,
        };

        // Apply filters
        if (appliedFilters.name) {
          params.name = appliedFilters.name;
        }
        if (appliedFilters.category) {
          params.category = appliedFilters.category;
        }
        if (appliedFilters.durationRange.min > 0) {
          params.durationMin = appliedFilters.durationRange.min;
        }
        if (appliedFilters.durationRange.max < 180) {
          params.durationMax = appliedFilters.durationRange.max;
        }
        if (appliedFilters.loadRange.min > 0) {
          params.loadMin = appliedFilters.loadRange.min;
        }
        if (appliedFilters.loadRange.max < 200) {
          params.loadMax = appliedFilters.loadRange.max;
        }
        // If favorites filter is active, tell backend to filter by user's favorites
        if (appliedFilters.favoritesOnly) {
          params.favoritesOnly = true;
        }

        const response = await apiClient.searchBikeWorkouts(params);

        if (response.success && response.data) {
          const { workouts: newWorkouts, total: totalCount, categories: cats } = response.data;

          // Transform workouts to WorkoutCardData format
          const transformedWorkouts: WorkoutCardData[] = newWorkouts.map(w => ({
            id: w.id,
            name: w.name,
            type: w.type,
            sportType: 'cycling' as const,
            category: w.category,
            total_duration: Math.round(w.minTime),
            TSS: w.load,
            steps: w.steps,
          }));

          if (resetList) {
            setWorkouts(transformedWorkouts);
          } else {
            setWorkouts(prev => [...prev, ...transformedWorkouts]);
          }

          setTotal(totalCount);
          setCategories(cats);
          setHasMore(skip + newWorkouts.length < totalCount);
        } else {
          throw new Error(response.message || 'Failed to load workouts');
        }
      } catch (err: any) {
        logger.error('Failed to load bike workouts', err);
        setError(err.message || 'Failed to load workouts');
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [appliedFilters]
  );

  // Initial load and when filters change
  useEffect(() => {
    fetchWorkouts(0, true);
  }, [fetchWorkouts]);

  // Infinite scroll observer - disabled when favorites filter is active (client-side filtering)
  useEffect(() => {
    // Don't use infinite scroll when filtering by favorites - we filter client-side
    if (appliedFilters.favoritesOnly) {
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          fetchWorkouts(workouts.length, false);
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading, workouts.length, fetchWorkouts, appliedFilters.favoritesOnly]);

  // Debounce timer ref for name search
  const nameDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Apply filters handler
  const handleApplyFilters = () => {
    setAppliedFilters({ ...filters });
  };

  // Handle name search with debounce
  const handleNameChange = (value: string) => {
    setFilters(prev => ({ ...prev, name: value }));

    // Clear existing timeout
    if (nameDebounceRef.current) {
      clearTimeout(nameDebounceRef.current);
    }

    // Set new timeout to apply filter after 500ms
    nameDebounceRef.current = setTimeout(() => {
      setAppliedFilters(prev => ({ ...prev, name: value }));
    }, 500);
  };

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (nameDebounceRef.current) {
        clearTimeout(nameDebounceRef.current);
      }
    };
  }, []);

  // Clear filters handler
  const handleClearFilters = () => {
    const defaultFilters: Filters = {
      name: '',
      category: '',
      durationRange: { min: 30, max: 60 },
      loadRange: { min: 30, max: 90 },
      favoritesOnly: false,
    };
    setFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
  };

  // Handlers
  const handleWorkoutClick = (workout: WorkoutCardData) => {
    navigate(`/workout-library/${workout.id}`);
  };

  // Duration range handlers
  const handleDurationChange = (min: number, max: number) => {
    setFilters(prev => ({ ...prev, durationRange: { min, max } }));
  };

  const handleDurationChangeCommitted = (min: number, max: number) => {
    setAppliedFilters(prev => ({ ...prev, durationRange: { min, max } }));
  };

  // Load range handlers
  const handleLoadChange = (min: number, max: number) => {
    setFilters(prev => ({ ...prev, loadRange: { min, max } }));
  };

  const handleLoadChangeCommitted = (min: number, max: number) => {
    setAppliedFilters(prev => ({ ...prev, loadRange: { min, max } }));
  };

  // Check if filters have changed from applied
  const filtersChanged =
    filters.name !== appliedFilters.name ||
    filters.category !== appliedFilters.category ||
    filters.durationRange.min !== appliedFilters.durationRange.min ||
    filters.durationRange.max !== appliedFilters.durationRange.max ||
    filters.loadRange.min !== appliedFilters.loadRange.min ||
    filters.loadRange.max !== appliedFilters.loadRange.max ||
    filters.favoritesOnly !== appliedFilters.favoritesOnly;

  // Workouts are now filtered on the backend (including favorites via ids param)
  const displayedWorkouts = workouts;

  if ((loading || favoritesLoading) && workouts.length === 0) {
    return <LoadingAnimation message="Loading workout library..." />;
  }

  if (error && workouts.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            mt: 4,
          }}
        >
          <Typography color="error">{error}</Typography>
          <Chip label="Retry" onClick={() => fetchWorkouts(0, true)} clickable />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <IconButton onClick={() => navigate('/')} aria-label="Go back to dashboard" size="small">
          <ArrowBack />
        </IconButton>
        <DirectionsBike color="primary" />
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          Bike Workout Library
        </Typography>
        <Chip label={`${total} workouts`} size="small" variant="outlined" sx={{ ml: 'auto' }} />
      </Box>

      {/* Filters */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          mb: 3,
          p: 2,
          backgroundColor: 'background.paper',
          borderRadius: 2,
          boxShadow: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          {/* Name Search */}
          <Box
            sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flexGrow: 1, minWidth: 200 }}
          >
            <Typography variant="caption" color="text.secondary">
              Name
            </Typography>
            <TextField
              value={filters.name}
              onChange={e => handleNameChange(e.target.value)}
              placeholder="Search by name..."
              size="small"
              aria-label="Search workouts by name"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 20, color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Category Dropdown */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 150 }}>
            <Typography variant="caption" color="text.secondary">
              Category
            </Typography>
            <Select
              value={filters.category}
              onChange={e => {
                const newValue = e.target.value;
                setFilters(prev => ({ ...prev, category: newValue }));
                setAppliedFilters(prev => ({ ...prev, category: newValue }));
              }}
              size="small"
              displayEmpty
              aria-label="Filter by workout category"
            >
              <MenuItem value="">All Categories</MenuItem>
              {categories.map(cat => (
                <MenuItem key={cat} value={cat}>
                  {cat}
                </MenuItem>
              ))}
            </Select>
          </Box>

          {/* Duration Range */}
          <RangeInputWithSlider
            label="Duration"
            unit="min"
            minValue={filters.durationRange.min}
            maxValue={filters.durationRange.max}
            min={0}
            max={120}
            step={5}
            marks={DURATION_MARKS}
            onChange={handleDurationChange}
            onChangeCommitted={handleDurationChangeCommitted}
            ariaLabel="Filter by duration range"
          />

          {/* Load Range */}
          <RangeInputWithSlider
            label="Load (TSS)"
            minValue={filters.loadRange.min}
            maxValue={filters.loadRange.max}
            min={0}
            max={200}
            step={5}
            marks={LOAD_MARKS}
            onChange={handleLoadChange}
            onChangeCommitted={handleLoadChangeCommitted}
            ariaLabel="Filter by load range"
          />
        </Box>

        {/* Favorites Toggle and Apply/Clear Buttons */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={filters.favoritesOnly}
                onChange={e => {
                  const newValue = e.target.checked;
                  setFilters(prev => ({ ...prev, favoritesOnly: newValue }));
                  setAppliedFilters(prev => ({ ...prev, favoritesOnly: newValue }));
                }}
                size="small"
                aria-label="Show favorites only"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Favorite sx={{ fontSize: 18, color: 'error.main' }} />
                <Typography variant="body2">Favorites only</Typography>
                {favoriteWorkouts.length > 0 && (
                  <Chip label={favoriteWorkouts.length} size="small" sx={{ height: 20 }} />
                )}
              </Box>
            }
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleClearFilters}
              aria-label="Clear all filters"
            >
              Clear Filters
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleApplyFilters}
              disabled={!filtersChanged}
              startIcon={<FilterList />}
              aria-label="Apply filters"
            >
              Apply Filters
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Workout Grid */}
      {displayedWorkouts.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            py: 8,
          }}
        >
          <Typography color="text.secondary">
            {appliedFilters.favoritesOnly
              ? 'No favorite workouts yet. Add some by clicking the heart icon!'
              : 'No workouts match your filters'}
          </Typography>
          <Chip label="Clear filters" onClick={handleClearFilters} clickable />
        </Box>
      ) : (
        <>
          <Grid container spacing={2}>
            {displayedWorkouts.map(workout => (
              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={workout.id}>
                <WorkoutCard
                  workout={workout}
                  onClick={handleWorkoutClick}
                  isFavorite={favoriteWorkouts.includes(workout.id)}
                  onToggleFavorite={handleToggleFavorite}
                />
              </Grid>
            ))}
          </Grid>

          {/* Infinite Scroll Trigger */}
          <Box
            ref={observerTarget}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              py: 4,
            }}
          >
            {loadingMore && <CircularProgress size={32} />}
            {!hasMore && workouts.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                All {total} workouts loaded
              </Typography>
            )}
          </Box>
        </>
      )}
    </Container>
  );
};

export default WorkoutLibrary;
